import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { useMemo } from "react";
import { api, EventRow, RunDetail } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { Json } from "../components/Json";

export function Run() {
  const { runId = "" } = useParams();

  const run = useQuery({
    queryKey: ["run", runId],
    queryFn: () => api<RunDetail>(`/api/runs/${runId}`),
    refetchInterval: (q) =>
      q.state.data?.status === "running" ? 2_000 : false,
    enabled: !!runId,
  });

  const events = useQuery({
    queryKey: ["run-events", runId],
    queryFn: () => api<EventRow[]>(`/api/runs/${runId}/events?limit=2000`),
    refetchInterval: (q) =>
      run.data?.status === "running" || !q.state.data ? 1_500 : false,
    enabled: !!runId,
  });

  const decoded = useMemo(
    () => decodeStream(events.data ?? []),
    [events.data],
  );

  if (run.isLoading) {
    return <p className="text-sm text-slate-500">Loading run…</p>;
  }
  if (run.error || !run.data) {
    return (
      <p className="text-sm text-rose-700">
        {run.error
          ? `Failed: ${(run.error as Error).message}`
          : "Run not found"}
      </p>
    );
  }
  const r = run.data;

  return (
    <div className="space-y-6">
      <div className="flex items-baseline gap-2 text-xs">
        <Link to="/" className="text-slate-500 hover:underline">
          tasks
        </Link>
        <span className="text-slate-300">/</span>
        <Link
          to={`/tasks/${encodeURIComponent(r.task_id)}`}
          className="text-slate-500 hover:underline font-mono"
        >
          {r.task_id}
        </Link>
        <span className="text-slate-300">/</span>
        <span className="text-slate-500 font-mono">{r.id.slice(0, 12)}</span>
      </div>

      {/* Header */}
      <section className="rounded border border-slate-200 bg-white p-4 space-y-2">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-lg font-semibold font-mono">{r.agent_name}</h1>
          <StatusBadge status={r.status} />
          <span className="text-xs text-slate-500">
            {durationLabel(r.started_at, r.ended_at)}
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <Field label="Started">{fmt(r.started_at)}</Field>
          <Field label="Ended">{fmt(r.ended_at)}</Field>
          <Field label="Input tokens">
            {r.usage?.input_tokens?.toLocaleString() ?? "—"}
          </Field>
          <Field label="Output tokens">
            {r.usage?.output_tokens?.toLocaleString() ?? "—"}
          </Field>
        </div>
        {r.error && (
          <pre className="mt-2 bg-rose-50 border border-rose-200 text-rose-800 text-xs rounded p-2 whitespace-pre-wrap">
            {r.error}
          </pre>
        )}
      </section>

      {/* Prompt input */}
      <Section title="Input">
        <Json value={r.input} />
      </Section>

      {/* Reconstructed thinking + assistant text */}
      {decoded.thinking.length > 0 && (
        <Section title={`Thinking (${decoded.thinking.length})`} collapsible>
          <div className="space-y-2">
            {decoded.thinking.map((t, i) => (
              <pre
                key={i}
                className="bg-slate-50 border border-slate-200 rounded p-2 text-xs whitespace-pre-wrap font-mono"
              >
                {t}
              </pre>
            ))}
          </div>
        </Section>
      )}

      {decoded.assistant.length > 0 && (
        <Section title="Messages">
          <div className="space-y-3">
            {decoded.assistant.map((m, i) => (
              <div
                key={i}
                className="bg-white border border-slate-200 rounded p-3 text-sm whitespace-pre-wrap"
              >
                {m}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Tool calls */}
      {decoded.tools.length > 0 && (
        <Section title={`Tool calls (${decoded.tools.length})`} collapsible>
          <ul className="space-y-1 text-xs font-mono">
            {decoded.tools.map((t, i) => (
              <li
                key={i}
                className="bg-slate-50 border border-slate-200 rounded px-2 py-1"
              >
                <span className="text-blue-700 font-semibold">{t.name}</span>
                {t.id && (
                  <span className="text-slate-400 ml-2">[{t.id}]</span>
                )}
                {t.input !== undefined && (
                  <div className="mt-1 text-slate-700">
                    {JSON.stringify(t.input).slice(0, 200)}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Output */}
      {r.output !== null && r.output !== undefined && (
        <Section title="Output">
          <Json value={r.output} />
        </Section>
      )}

      {/* Turn usage */}
      {r.usage?.turn_usage && r.usage.turn_usage.length > 0 && (
        <Section title="Per-turn usage">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-2 py-1">Turn</th>
                <th className="text-right px-2 py-1">Input</th>
                <th className="text-right px-2 py-1">Output</th>
                <th className="text-right px-2 py-1">Cache read</th>
                <th className="text-right px-2 py-1">Cache write</th>
              </tr>
            </thead>
            <tbody>
              {r.usage.turn_usage.map((t, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-2 py-1">{t.index}</td>
                  <td className="text-right px-2 py-1 tabular-nums">
                    {t.input_tokens.toLocaleString()}
                  </td>
                  <td className="text-right px-2 py-1 tabular-nums">
                    {t.output_tokens.toLocaleString()}
                  </td>
                  <td className="text-right px-2 py-1 tabular-nums">
                    {t.cache_read_input_tokens?.toLocaleString() ?? "—"}
                  </td>
                  <td className="text-right px-2 py-1 tabular-nums">
                    {t.cache_creation_input_tokens?.toLocaleString() ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {/* Raw event log */}
      <Section title={`Events (${events.data?.length ?? 0})`} collapsible defaultOpen={false}>
        <ol className="space-y-1 text-xs font-mono max-h-96 overflow-auto">
          {(events.data ?? []).map((e) => (
            <li
              key={e.seq}
              className="border-b border-slate-100 py-1 flex gap-2"
            >
              <span className="text-slate-400">#{e.seq}</span>
              <span className="text-blue-700">{e.kind}</span>
              <span className="text-slate-500 truncate flex-1">
                {JSON.stringify(e.body).slice(0, 120)}
              </span>
            </li>
          ))}
        </ol>
      </Section>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-slate-500">{label}</div>
      <div className="font-mono">{children}</div>
    </div>
  );
}

function Section({
  title,
  children,
  collapsible,
  defaultOpen = true,
}: {
  title: string;
  children: React.ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
}) {
  return (
    <details open={!collapsible || defaultOpen}>
      <summary
        className={`font-semibold text-sm mb-2 ${
          collapsible ? "cursor-pointer select-none" : "cursor-default"
        }`}
      >
        {title}
      </summary>
      <div className="ml-1">{children}</div>
    </details>
  );
}

function fmt(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(+d)) return iso;
  return d.toLocaleString();
}

function durationLabel(a: string | null, b: string | null): string {
  if (!a) return "";
  const start = +new Date(a);
  const end = b ? +new Date(b) : Date.now();
  const ms = Math.max(0, end - start);
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  return `${Math.floor(s / 60)}m ${Math.floor(s % 60)}s`;
}

// Decode claude stream-json frames out of RuntimeEvent::StreamMessage
// bodies. The wire shape is pinned by tests in swarm-runtime/src/lib.rs
// (`runtime_event_wire_format_*`) — internally tagged with snake_case
// names:
//
//   { "type": "stream_message", "json": { ...claude frame... } }
//
// `kind === "stream"` is what the orchestrator's pump writes for these
// rows; `body.type` must equal "stream_message". If either drifts, the
// runtime tests fail in CI before this decoder breaks silently.
interface ToolCall {
  name: string;
  id?: string;
  input?: unknown;
}
interface Decoded {
  thinking: string[];
  assistant: string[];
  tools: ToolCall[];
}

interface StreamMessageBody {
  type: "stream_message";
  json: ClaudeFrame;
}

interface ClaudeFrame {
  type?: string;
  message?: {
    content?: Array<{
      type: string;
      thinking?: string;
      text?: string;
      name?: string;
      id?: string;
      input?: unknown;
    }>;
  };
}

function decodeStream(events: EventRow[]): Decoded {
  const out: Decoded = { thinking: [], assistant: [], tools: [] };
  for (const ev of events) {
    if (ev.kind !== "stream") continue;
    const body = ev.body as StreamMessageBody | undefined;
    if (body?.type !== "stream_message") continue;
    const frame = body.json;
    if (frame?.type !== "assistant") continue;
    const blocks = frame.message?.content;
    if (!Array.isArray(blocks)) continue;
    for (const b of blocks) {
      if (b.type === "thinking" && typeof b.thinking === "string") {
        out.thinking.push(b.thinking);
      } else if (b.type === "text" && typeof b.text === "string") {
        out.assistant.push(b.text);
      } else if (b.type === "tool_use" && typeof b.name === "string") {
        out.tools.push({ name: b.name, id: b.id, input: b.input });
      }
    }
  }
  return out;
}
